import type { CarriersCarrierListResponse } from "@platform/api";
import type { RfpCarriersResponse } from "@platform/hooks";

/**
 * Builds a shareIdentifier → display-name map from the RFP carriers response.
 * A proposal's carrierId is the carrier's shareIdentifier, so this resolves the
 * slug btx now returns in carrierName (e.g. "guardian86745") to a real name.
 */
export const buildCarrierNameById = (
  data: RfpCarriersResponse,
): Map<string, string> => {
  const allCarriers = [
    ...(data.connected ?? []),
    ...(data.unconnected ?? []),
    ...(data.unknown ?? []),
  ];

  return new Map(
    allCarriers.flatMap((carrier) =>
      carrier.shareIdentifier
        ? [[carrier.shareIdentifier, carrier.name] as const]
        : [],
    ),
  );
};

/**
 * Builds a carrierId → display-name map from the full centro-portal carrier
 * directory, whose `id` equals the BTX proposal carrierId — so it resolves every
 * real carrier, including ones not invited to this RFP. Module-level so React
 * Query memoizes the select result (an inline select returns a new Map each
 * render and would thrash the grid).
 */
export const buildCarrierNameByIdFromDirectory = (
  data: CarriersCarrierListResponse,
): Map<string, string> =>
  new Map(data.carriers.map((carrier) => [carrier.id, carrier.name]));

/**
 * Resolves each proposal's carrierName to a display name. Prefers the map's
 * resolved name, then the proposal's existing value (the slug btx returns), and
 * finally the carrierId — so a carrier super-group header is never blank, even
 * while the carrier map is still loading or the proposal arrives without a name.
 * Done once at the data boundary so every downstream grid/rates/sequence adapter
 * renders the resolved name without each having to take the map.
 */
export const resolveProposalCarrierNames = <
  T extends { carrierId: string; carrierName: string },
>(
  proposals: T[],
  carrierNameById: Map<string, string> | undefined,
): T[] => {
  let changed = false;
  const next = proposals.map((proposal) => {
    const resolvedName = carrierNameById?.get(proposal.carrierId);
    const carrierName =
      resolvedName || proposal.carrierName || proposal.carrierId;
    if (carrierName === proposal.carrierName) return proposal;
    changed = true;
    return { ...proposal, carrierName };
  });
  return changed ? next : proposals;
};
